import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, InlineNotification, Link, Loading } from "@carbon/react";
import { ArrowRight } from "@carbon/icons-react";
import { validationSchema } from "../../utils";
import { useErrors } from "../../hooks";
import AuthLayout from "../carbon/AuthLayout/AuthLayout";
import CarbonForm from "../carbon/Form/Form";
import TextField from "../carbon/Form/TextField";
import { useAuth, useUser } from "../../context";
import "./loginForm.scss";

type FormValues = {
  email: string;
  password: string;
};

/**
 * The login screen (`POST /auth/login` via `AuthProvider#authSignIn`), rebuilt on the Carbon
 * primitives (Task 6). Contract unchanged: `useAuth()` for `authSignIn`/`isLoading`/`error`,
 * `useUser()` for `user`, `validationSchema` (utils/validations.ts) via `yupResolver`,
 * `useErrors(error)` for the translated message, links to `/reset-password` and `/register`.
 *
 * LAYOUT: renders through `AuthLayout` with `brandPanel` — a DARK (Carbon g100) two-column shell.
 * The form floats directly on the near-black background in the left column (a "Sign In" heading +
 * a "create an account" subtitle above the fields), and a full-height dark brand panel with the
 * white Simbuy wordmark fills the right (dropped below tablet). The auth flow itself is unchanged
 * (single-step email + password → `authSignIn`); only the chrome went dark.
 */
const LoginForm = () => {
  const { user } = useUser();
  const { authSignIn, isLoading, error } = useAuth();
  const { t } = useTranslation();
  const { errorMessage } = useErrors(error);
  const { control, handleSubmit } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    // Explicit defaultValues (not just the resolver): a Controller-bound field with none falls
    // back to `undefined`, not the empty string an uncontrolled `register()` input's native
    // `value` defaults to. Yup's own per-field tests (`.matches`, `.min`, ...) skip on `undefined`
    // ("absent") and defer entirely to `.required()`'s message, but they DO run against `""`. An
    // empty-string default keeps Yup's error selection identical to the pre-Carbon form —
    // verified against e2e/unauthenticated.spec.ts, which pins the exact message.
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = (data: FormValues) => {
    authSignIn({
      email: data.email,
      password: data.password,
    });
  };

  if (isLoading && !user?.id) {
    return <Loading withOverlay description={t("cta.loading")} />;
  }

  return (
    <AuthLayout brandPanel>
      <div className="login-form__header">
        <h1 className="login-form__title">{t("login.signIn")}</h1>
        <p className="login-form__subtitle">
          {t("login.noAccount")}{" "}
          <Link as={RouterLink} to="/register">
            {t("login.create")}
          </Link>
        </p>
      </div>

      <CarbonForm
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        noValidate
        className="login-form"
        autoComplete="off"
      >
        <TextField
          name="email"
          type="email"
          label={t("login.email")}
          control={control}
          required
        />

        <TextField
          name="password"
          type="password"
          label={t("login.password")}
          control={control}
          required
        />

        {error ? (
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title={errorMessage}
            className="login-form__notification"
          />
        ) : null}

        <div className="login-form__forgot">
          <Link as={RouterLink} to="/reset-password">
            {t("login.forgot")}
          </Link>
        </div>

        <Button
          type="submit"
          className="login-form__submit"
          renderIcon={ArrowRight}
        >
          {t("login.login")}
        </Button>
      </CarbonForm>
    </AuthLayout>
  );
};

export default LoginForm;
